import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  ChevronsUpDown,
  X,
  Search,
  Check,
  Loader2,
  AlertTriangle,
  Users,
} from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from 'src/components/ui/popover';
import { cn } from 'src/lib/utils';
import { getInitials, paletteForSeed } from 'src/lib/avatar';

// Tiny initials circle shared by trigger chips (14px) and option rows
// (28px) — same deterministic palette CandidateCell uses, so an
// interviewer's color stays consistent with how they'd read anywhere else
// in the app a person gets an avatar. Defined outside the component (not
// nested) so it isn't recreated — and its DOM remounted — on every render.
const Avatar = ({ seed, label, size }) => {
  const { bg, text } = paletteForSeed(seed || label || '');
  return (
    <span
      className={cn(
        'shrink-0 flex items-center justify-center rounded-full font-semibold',
        bg,
        text,
        size === 'sm' ? 'h-3.5 w-3.5 text-[8px]' : 'h-7 w-7 text-[10.5px]'
      )}
    >
      {getInitials(label)}
    </span>
  );
};

// Async, server-side searchable, infinite-scroll multi-select dropdown.
// Built on this app's own Radix Popover primitive (src/components/ui/
// popover.jsx) rather than a hand-rolled portal+fixed-position panel — that
// hand-rolled version (still visible in git history) had to fight Radix
// Dialog's modality piecemeal every time this field was used inside an
// action modal (ActionPayloadModal): first the panel got clipped by
// DialogBody's own overflow, then — once portaled to escape that — Radix's
// body scroll-lock silently blocked wheel-scrolling it, then its
// pointer-events:none lockout made the whole panel unclickable to real
// input (only `.click()` calls bypass pointer-events, which is why testing
// via those didn't catch it), and finally its FocusScope kept yanking
// keyboard focus back out of the search input the instant it landed there
// — four separate fights against the same root cause, confirmed live one
// at a time. Radix's own Popover is designed to nest inside a Radix Dialog
// correctly (a documented, supported pattern — they share the same
// underlying focus-guard/dismissable-layer coordination), so rebuilding on
// it resolves all four at once instead of patching a fifth: positioning,
// scroll, pointer-events and focus are Radix's problem now, not this
// component's.
//
// Props:
// - fetcher: ({ page, pageSize, search }) => Promise<{ items: any[], hasNext?: boolean }>
// - getOptionLabel: (item) => string
// - getOptionValue: (item) => string | number
// - getOptionAvatarSeed?: (item) => string — opts into the avatar-initials
//   treatment below (trigger chips + option rows both get a colored initials
//   circle, same palette CandidateCell uses). Omit for a plain text list —
//   nothing here assumes every consumer is picking people.
// - getOptionSubLabel?: (item) => string | undefined — a muted second line
//   under an option's label (e.g. email), only rendered in avatar mode.
// - value: Array<{ label: string, value: string | number }>
// - onChange: (options: Array<{ label: string, value: string | number }>) => void
// - placeholder?: string
// - maxDisplay?: number
// - pageSize?: number (default 10)
export function AsyncMultiSelectDropdown({
  fetcher,
  getOptionLabel,
  getOptionValue,
  getOptionAvatarSeed,
  getOptionSubLabel,
  value,
  onChange,
  placeholder = 'Select options... ',
  maxDisplay = 3,
  pageSize = 10,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]); // Array<{label,value,item}>
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const hasFetchedRef = useRef(false);

  const hasAvatars = !!getOptionAvatarSeed;
  const hasMore = useMemo(() => hasNext, [hasNext]);

  // Define fetchPage before effects to satisfy no-use-before-define
  const fetchPage = useCallback(
    async (requestedPage, replace) => {
      try {
        setIsLoading(true);
        setError(null);
        const { items, hasNext: remoteHasNext } = await fetcher({
          page: requestedPage,
          pageSize,
          search: searchTerm,
        });

        const mapped = (items || []).map((item) => ({
          label: getOptionLabel(item),
          value: getOptionValue(item),
          subLabel: getOptionSubLabel ? getOptionSubLabel(item) : undefined,
          avatarSeed: getOptionAvatarSeed ? getOptionAvatarSeed(item) : null,
          item,
        }));

        setOptions((prev) => {
          const next = replace ? mapped : [...prev, ...mapped];
          // dedupe by value without for..of to satisfy lint rules
          const seen = new Set();
          const deduped = [];
          next.forEach((opt) => {
            const key = String(opt.value);
            if (!seen.has(key)) {
              seen.add(key);
              deduped.push(opt);
            }
          });
          return deduped;
        });
        // Prefer hasNext from API, fallback to heuristic: full page = might have more
        const calculatedHasNext =
          remoteHasNext !== undefined
            ? remoteHasNext
            : (items?.length || 0) >= pageSize;
        setHasNext(calculatedHasNext);
        setPage(requestedPage);
      } catch (err) {
        console.error('[AsyncMultiSelect] Error:', err);
        setError(err?.message || 'Failed to load options');
      } finally {
        setIsLoading(false);
      }
    },
    [
      fetcher,
      getOptionLabel,
      getOptionValue,
      getOptionSubLabel,
      getOptionAvatarSeed,
      pageSize,
      searchTerm,
    ]
  );

  useEffect(() => {
    if (!isOpen && searchTerm) {
      // Clear search term when dropdown closes
      setSearchTerm('');
    }
  }, [isOpen, searchTerm]);

  // Debounce search - only when search term changes while open
  const debouncedSearch = useRef();
  const previousSearchTerm = useRef(searchTerm);
  useEffect(() => {
    if (!isOpen) {
      previousSearchTerm.current = searchTerm;
      return () => {};
    }

    // Only trigger if search term actually changed
    if (previousSearchTerm.current === searchTerm) {
      return () => {};
    }

    previousSearchTerm.current = searchTerm;

    if (debouncedSearch.current) clearTimeout(debouncedSearch.current);
    debouncedSearch.current = setTimeout(() => {
      // Reset and fetch page 1 on new search
      setOptions([]);
      setPage(1);
      setHasNext(false);
      hasFetchedRef.current = false; // Reset fetch flag on new search
      fetchPage(1, true);
    }, 300);
    return () => {
      if (debouncedSearch.current) clearTimeout(debouncedSearch.current);
    };
  }, [isOpen, searchTerm, fetchPage]);

  // Initial open -> fetch first page
  useEffect(() => {
    if (
      isOpen &&
      options.length === 0 &&
      !isLoading &&
      !hasFetchedRef.current
    ) {
      hasFetchedRef.current = true;
      fetchPage(1, true);
    }
  }, [isOpen, options.length, isLoading, fetchPage]);

  // fetchPage defined above

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const threshold = 0.85; // 85%
    const atBottom =
      (el.scrollTop + el.clientHeight) / el.scrollHeight >= threshold;
    if (atBottom && !isLoading && hasMore) {
      fetchPage(page + 1, false);
    }
  };

  // Rebuilding on Radix's Popover (see the file-level comment) resolved the
  // pointer-events and focus fights against the parent Dialog's modality
  // for free, but NOT this one: confirmed live that a wheel event over this
  // list still comes back `defaultPrevented: true`, because Dialog's own
  // react-remove-scroll body-lock doesn't automatically allow-list an
  // arbitrary sibling Popover the way FocusScope/DismissableLayer's shared
  // coordination does — that allow-listing is a separate mechanism keyed
  // off its own `RemoveScroll` wrapper, which Popover.Content doesn't use.
  // Scrolling scrollTop by hand sidesteps it the same way it did on the
  // hand-rolled version: a direct property write isn't the "default
  // action" a preventDefault'd wheel event suppresses.
  const handleWheel = (e) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop += e.deltaY;
  };

  const selectedOptions = value || [];

  const toggleOption = (option) => {
    const exists = selectedOptions.some((item) => item.value === option.value);
    const newSelection = exists
      ? selectedOptions.filter((item) => item.value !== option.value)
      : [...selectedOptions, option];
    onChange(newSelection);
  };

  const removeOption = (option) => {
    onChange(selectedOptions.filter((item) => item.value !== option.value));
  };

  const handleKeyDown = (e, callback) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground-strong placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
        >
          <div className="flex flex-wrap items-center gap-1">
            {selectedOptions.length === 0 && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                {hasAvatars && <Users className="h-3.5 w-3.5 opacity-70" />}
                {placeholder}
              </span>
            )}
            {selectedOptions.slice(0, maxDisplay).map((option) => (
              <div
                key={option.value}
                className="flex items-center gap-1 rounded-md bg-secondary py-0.5 pl-1 pr-1.5 text-xs text-secondary-foreground"
              >
                {hasAvatars && (
                  <Avatar
                    seed={option.avatarSeed}
                    label={option.label ?? String(option.value)}
                    size="sm"
                  />
                )}
                {option.label ?? option.value}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(option);
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    handleKeyDown(e, () => removeOption(option));
                  }}
                  className="ml-0.5 text-secondary-foreground hover:text-primary cursor-pointer"
                  aria-label={`Remove ${option.label ?? option.value}`}
                >
                  <X size={12} />
                </span>
              </div>
            ))}
            {selectedOptions.length > maxDisplay && (
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                +{selectedOptions.length - maxDisplay} more
              </span>
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)] p-0"
        // Radix would otherwise auto-focus its first focusable descendant
        // (here, the search input anyway, but relying on Radix's default
        // rather than being explicit about it is what this whole rebuild
        // moved away from) — take it over explicitly instead so opening
        // this dropdown always focuses search immediately, matching the
        // hand-rolled version's own behavior.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex h-9 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Search options"
          />
        </div>
        <div
          ref={listRef}
          className="max-h-[300px] overflow-auto p-1"
          onScroll={handleScroll}
          onWheel={handleWheel}
          role="listbox"
          aria-label="Options list"
        >
          {options.map((option) => {
            const selected = selectedOptions.some(
              (item) => item.value === option.value
            );
            return (
              <div
                key={option.value}
                role="option"
                tabIndex={0}
                aria-selected={selected}
                onClick={() => toggleOption(option)}
                onKeyDown={(e) => handleKeyDown(e, () => toggleOption(option))}
                className={cn(
                  'relative flex cursor-default select-none items-center gap-2.5 rounded-md outline-none transition-colors',
                  hasAvatars ? 'px-2 py-2' : 'px-2 py-1.5 text-sm',
                  selected
                    ? 'bg-primary/[0.06]'
                    : 'hover:bg-accent hover:text-accent-foreground'
                )}
              >
                {hasAvatars ? (
                  <>
                    <Avatar seed={option.avatarSeed} label={option.label} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-medium leading-tight text-foreground-strong">
                        {option.label}
                      </p>
                      {option.subLabel && (
                        <p className="truncate text-[11.5px] leading-tight text-muted-foreground">
                          {option.subLabel}
                        </p>
                      )}
                    </div>
                    {selected && (
                      <Check className="h-4 w-4 shrink-0 text-primary" />
                    )}
                  </>
                ) : (
                  <>
                    <div className="h-4 w-4 shrink-0 rounded-sm border border-primary flex items-center justify-center">
                      {selected && <Check className="h-3 w-3 text-primary" />}
                    </div>
                    <span>{option.label}</span>
                  </>
                )}
              </div>
            );
          })}

          {isLoading && (
            <div className="py-3 text-center text-sm flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading...
            </div>
          )}

          {!isLoading && options.length === 0 && !error && (
            <div className="flex flex-col items-center gap-1.5 py-6 text-center text-sm text-muted-foreground">
              {hasAvatars ? (
                <Users className="h-5 w-5 opacity-40" />
              ) : (
                <Search className="h-5 w-5 opacity-40" />
              )}
              No results found.
            </div>
          )}

          {!isLoading && error && (
            <div className="flex flex-col items-center gap-1.5 py-6 text-center text-sm text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {error}
            </div>
          )}

          {!isLoading && !hasMore && options.length > 0 && (
            <div className="py-2 text-center text-[11px] text-muted-foreground">
              End of results
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
